import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getToken, keycloak } from './keycloak';

// In development, use the Vite proxy to avoid CORS issues
// In production, use the full API URL
const API_URL = import.meta.env.DEV ? '/api/v1' : (import.meta.env.VITE_API_URL || '/api/v1');

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = getToken();

    if (token) {
      // Check if token needs refresh (expires in less than 30 seconds)
      if (keycloak.isTokenExpired(30)) {
        try {
          await keycloak.updateToken(30);
        } catch {
          keycloak.login();
          return Promise.reject(new Error('Token refresh failed'));
        }
      }

      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          keycloak.login();
          break;
        case 403:
          console.error('Access forbidden');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error');
          break;
      }
    }

    return Promise.reject(error);
  }
);

export default api;

/**
 * Extrait le message d'erreur backend depuis une erreur axios.
 * Le backend BPA renvoie : { success: false, error: { code, message } }.
 * Fallback : err.message générique d'axios.
 */
export function extractApiError(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as {
      response?: { data?: { error?: { message?: string }; message?: string | string[] } };
      message?: string;
    };
    const data = e.response?.data;
    if (data?.error?.message) return data.error.message;
    if (typeof data?.message === 'string') return data.message;
    if (Array.isArray(data?.message)) return data.message.join(' · ');
    if (e.message) return e.message;
  }
  return 'Une erreur est survenue';
}
